// Centralized API Configuration
// This file manages the API base URL for the entire application

interface ApiConfig {
  baseUrl: string;
  timeout: number;
  retryAttempts: number;
}

/** Trimmed env base, or empty if unset. */
function apiBaseFromEnv(): string {
  const raw = import.meta.env.VITE_API_BASE_URL;
  if (typeof raw !== 'string' || raw.trim() === '') return '';
  return raw.trim().replace(/\/$/, '');
}

function isLocalBrowserHostname(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]';
}

/** True if URL points at this machine’s API (loopback), not staging/production hosts. */
function isLoopbackApiBase(url: string): boolean {
  try {
    const withScheme = /^https?:\/\//i.test(url) ? url : `http://${url}`;
    const u = new URL(withScheme);
    return u.hostname === 'localhost' || u.hostname === '127.0.0.1' || u.hostname === '[::1]';
  } catch {
    return false;
  }
}

/**
 * When the SPA is served over HTTPS, upgrade http API bases to https so the browser
 * does not block mixed active content. Loopback dev URLs stay on http.
 */
function normalizeApiBaseUrl(baseUrl: string): string {
  const trimmed = baseUrl.trim().replace(/\/$/, '');
  if (!trimmed || typeof window === 'undefined') return trimmed;
  if (window.location.protocol !== 'https:') return trimmed;
  if (isLoopbackApiBase(trimmed)) return trimmed;

  try {
    const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `http://${trimmed}`;
    const parsed = new URL(withScheme);
    if (parsed.protocol === 'http:') {
      parsed.protocol = 'https:';
      return parsed.origin;
    }
  } catch {
    /* ignore */
  }
  return trimmed;
}

/**
 * When VITE_API_BASE_URL is not baked in at build time, dev keeps using Kestrel’s default port.
 * Production builds default to the current page origin so integrated hosting (same site as API)
 * keeps working without env vars or localStorage — a common reason login “suddenly” breaks after
 * a new browser, cleared storage, or first visit from another machine.
 *
 * If you open the SPA from localhost but the bundle was built with a production VITE_API_BASE_URL,
 * we still use local Kestrel (unless the baked URL is explicitly loopback).
 */
function defaultApiBaseUrl(): string {
  if (isLocalBrowserHostname()) {
    const fromEnv = apiBaseFromEnv();
    if (fromEnv && isLoopbackApiBase(fromEnv)) return fromEnv;
    return 'http://localhost:5127';
  }

  const fromEnv = apiBaseFromEnv();
  if (fromEnv) return fromEnv;
  if (import.meta.env.DEV) return 'http://localhost:5127';
  if (typeof window !== 'undefined' && window.location?.origin && window.location.origin !== 'null') {
    return window.location.origin;
  }
  return 'http://localhost:5127';
}

class ApiConfigManager {
  private static instance: ApiConfigManager;
  private config: ApiConfig;
  private readonly CONFIG_KEY = 'appApiConfig';

  private getDefaultConfig(): ApiConfig {
    return {
      baseUrl: normalizeApiBaseUrl(defaultApiBaseUrl()),
      timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '') || 30000,
      retryAttempts: parseInt(import.meta.env.VITE_API_RETRY_ATTEMPTS || '') || 3
    };
  }

  private constructor() {
    this.config = this.loadConfig();
  }

  public static getInstance(): ApiConfigManager {
    if (!ApiConfigManager.instance) {
      ApiConfigManager.instance = new ApiConfigManager();
    }
    return ApiConfigManager.instance;
  }

  /**
   * Load configuration from localStorage or use defaults
   */
  private loadConfig(): ApiConfig {
    const defaults = this.getDefaultConfig();
    try {
      const stored = localStorage.getItem(this.CONFIG_KEY);
      if (stored) {
        const parsedConfig = JSON.parse(stored) as Partial<ApiConfig>;
        const merged = { ...defaults, ...parsedConfig };
        // Local SPA (Vite or production bundle on localhost): never keep a production baseUrl from localStorage.
        if (import.meta.env.DEV || isLocalBrowserHostname()) {
          merged.baseUrl = defaults.baseUrl;
        } else {
          merged.baseUrl = normalizeApiBaseUrl(merged.baseUrl);
        }
        return merged;
      }
    } catch (error) {
      console.warn('Failed to load API config from localStorage:', error);
    }

    return { ...defaults };
  }

  /**
   * Save configuration to localStorage
   */
  private saveConfig(): void {
    try {
      localStorage.setItem(this.CONFIG_KEY, JSON.stringify(this.config));
      console.log('API config saved:', this.config);
    } catch (error) {
      console.error('Failed to save API config:', error);
    }
  }

  /**
   * Get current API base URL
   */
  public getBaseUrl(): string {
    return normalizeApiBaseUrl(this.config.baseUrl);
  }

  /**
   * Set API base URL and persist it
   */
  public setBaseUrl(baseUrl: string): void {
    const normalized = normalizeApiBaseUrl(baseUrl.trim().replace(/\/$/, ''));
    if (isLocalBrowserHostname() && !isLoopbackApiBase(normalized)) {
      const local = defaultApiBaseUrl();
      console.warn('[apiConfig] Ignoring remote API base on localhost:', normalized, '→', local);
      this.config.baseUrl = local;
    } else {
      this.config.baseUrl = normalized;
    }
    this.saveConfig();

    // Notify listeners about the change
    this.notifyConfigChange();
  }

  /**
   * Get full API configuration
   */
  public getConfig(): ApiConfig {
    return { ...this.config, baseUrl: this.getBaseUrl() };
  }

  /**
   * Update full configuration
   */
  public updateConfig(newConfig: Partial<ApiConfig>): void {
    const next = { ...this.config, ...newConfig };
    if (typeof next.baseUrl === 'string') {
      next.baseUrl = normalizeApiBaseUrl(next.baseUrl.trim().replace(/\/$/, ''));
    }
    if (
      typeof next.baseUrl === 'string' &&
      isLocalBrowserHostname() &&
      !isLoopbackApiBase(next.baseUrl)
    ) {
      next.baseUrl = defaultApiBaseUrl();
    }
    this.config = next;
    this.saveConfig();
    this.notifyConfigChange();
  }

  /**
   * Reset to default configuration
   */
  public resetToDefault(): void {
    this.config = { ...this.getDefaultConfig() };
    this.saveConfig();
    this.notifyConfigChange();
  }

  /**
   * Notify all listeners about configuration changes
   */
  private notifyConfigChange(): void {
    // Dispatch custom event for components to listen to
    window.dispatchEvent(new CustomEvent('apiConfigChanged', {
      detail: { config: this.config }
    }));
  }

  /**
   * Add listener for configuration changes
   */
  public onConfigChange(callback: (config: ApiConfig) => void): () => void {
    const handler = (event: CustomEvent) => {
      callback(event.detail.config);
    };

    window.addEventListener('apiConfigChanged', handler as EventListener);

    // Return cleanup function
    return () => {
      window.removeEventListener('apiConfigChanged', handler as EventListener);
    };
  }
}

// Export singleton instance
export const apiConfig = ApiConfigManager.getInstance();

// Export types
export type { ApiConfig };